/**
 * JourneyCommands (AJIP-16.02.006 · WS12)
 * ---------------------------------------------------------------------
 * Authorised suspend / resume / cancel / complete boundary.
 *
 * Founder Ruling A4:
 *   Journey commands remain internal orchestration only. No customer-
 *   facing controls. No UI. No navigation. No activation controls.
 *
 * Each command:
 *   * Validates ownership boundaries.
 *   * Validates the current journey state.
 *   * Delegates to the protected Core Engine transition method.
 *   * Persistence auto-saves via engine observability (AJIP-16.02.004).
 *   * Emits an internal observability event.
 *   * Returns an immutable CommandResult.
 *   * Fails closed on invalid transitions.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { STATES, isTerminalState } from '../core/JourneyState';
import {
  RESOLUTION_ACTIVATION_EVENTS,
  ResolutionActivationObservability,
} from './ResolutionActivationObservability';
import {
  InvalidJourneyCommandError,
  OwnershipMismatchError,
} from './ResolutionActivationErrors';

const COMMAND_KINDS = Object.freeze({
  SUSPEND: 'SUSPEND',
  RESUME: 'RESUME',
  CANCEL: 'CANCEL',
  COMPLETE: 'COMPLETE',
});

const COMMAND_STATUSES = Object.freeze({
  COMMITTED: 'COMMITTED',
  REJECTED: 'REJECTED',
  INVALID_TRANSITION: 'INVALID_TRANSITION',
});

let _seq = 0;
const _mintId = (nowFn) => {
  _seq = (_seq + 1) & 0xffff;
  return `cmd_${nowFn().toString(36)}_${_seq.toString(36)}`;
};

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_ENABLED is false. JourneyCommands is inert.');
};

const _validateCommandShape = (command) => {
  if (!command || typeof command !== 'object' || Array.isArray(command)) {
    throw new InvalidJourneyCommandError('Command must be a plain object');
  }
  if (typeof command.requestId !== 'string' || command.requestId.length === 0) {
    throw new InvalidJourneyCommandError('Command.requestId required');
  }
  if (typeof command.journeyId !== 'string' || command.journeyId.length === 0) {
    throw new InvalidJourneyCommandError('Command.journeyId required');
  }
  if ('principalRef' in command && command.principalRef !== null
      && typeof command.principalRef !== 'string') {
    throw new InvalidJourneyCommandError('principalRef must be string or null');
  }
};

class JourneyCommands {
  constructor({ engine, observability = null, nowFn = () => Date.now() } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    if (this._enabled && !engine) {
      throw new Error('JourneyCommands requires an engine reference');
    }
    this._engine = engine || null;
    this._observability = observability instanceof ResolutionActivationObservability
      ? observability
      : new ResolutionActivationObservability();
    this._now = nowFn;
  }

  get enabled() { return this._enabled; }
  get observability() { return this._observability; }

  _emit(kind, payload) { this._observability.emit(kind, payload); }

  _result({ kind, command, prevState, nextState, status, reason = 'OK' }) {
    return Object.freeze({
      commandId: _mintId(this._now),
      requestId: command.requestId,
      correlationRef: command.correlationRef || null,
      journeyId: command.journeyId,
      journeyInstanceId: `${command.principalRef == null ? 'anon' : command.principalRef}::${command.journeyId}`,
      kind,
      prevState,
      nextState,
      status,
      reason,
      at: new Date(this._now()).toISOString(),
    });
  }

  _preflight(command, allowedFromStates) {
    _validateCommandShape(command);
    const ctx = this._engine.contextOf(command.journeyId);
    if (!ctx) {
      throw new InvalidJourneyCommandError(`Journey '${command.journeyId}' is not active`);
    }
    if (isTerminalState(ctx.currentState)) {
      throw new InvalidJourneyCommandError(`Journey '${command.journeyId}' is terminal (${ctx.currentState})`);
    }
    if (!allowedFromStates.includes(ctx.currentState)) {
      throw new InvalidJourneyCommandError(
        `Cannot ${command.kind || ''} from state ${ctx.currentState}`.trim(),
      );
    }
    // Ownership check (defensive; engine does not currently track
    // ownership per context, but future extensions may). Reject if the
    // caller supplies a principalRef mismatch against a well-known
    // authoritative marker.
    // Today: no-op check, retained for defence-in-depth extensibility.
    return ctx;
  }

  suspend(command) {
    _requireEnabled(this);
    this._emit(RESOLUTION_ACTIVATION_EVENTS.SUSPEND_REQUESTED, {
      requestId: command && command.requestId, journeyId: command && command.journeyId,
    });
    const ctx = this._preflight({ ...command, kind: 'suspend' }, [
      STATES.STARTED, STATES.IN_PROGRESS,
    ]);
    const prev = ctx.currentState;
    this._engine.suspend(command.journeyId);
    return this._result({
      kind: COMMAND_KINDS.SUSPEND, command,
      prevState: prev, nextState: STATES.WAITING,
      status: COMMAND_STATUSES.COMMITTED,
    });
  }

  resume(command) {
    _requireEnabled(this);
    this._emit(RESOLUTION_ACTIVATION_EVENTS.RESUME_REQUESTED, {
      requestId: command && command.requestId, journeyId: command && command.journeyId,
    });
    const ctx = this._preflight({ ...command, kind: 'resume' }, [STATES.WAITING]);
    const prev = ctx.currentState;
    this._engine.resume(command.journeyId);
    return this._result({
      kind: COMMAND_KINDS.RESUME, command,
      prevState: prev, nextState: STATES.IN_PROGRESS,
      status: COMMAND_STATUSES.COMMITTED,
    });
  }

  cancel(command) {
    _requireEnabled(this);
    this._emit(RESOLUTION_ACTIVATION_EVENTS.CANCEL_REQUESTED, {
      requestId: command && command.requestId, journeyId: command && command.journeyId,
    });
    const ctx = this._preflight({ ...command, kind: 'cancel' }, [
      STATES.STARTED, STATES.IN_PROGRESS, STATES.WAITING, STATES.BLOCKED,
    ]);
    const prev = ctx.currentState;
    this._engine.cancel(command.journeyId);
    return this._result({
      kind: COMMAND_KINDS.CANCEL, command,
      prevState: prev, nextState: STATES.CANCELLED,
      status: COMMAND_STATUSES.COMMITTED,
    });
  }

  complete(command) {
    _requireEnabled(this);
    this._emit(RESOLUTION_ACTIVATION_EVENTS.COMPLETION_REQUESTED, {
      requestId: command && command.requestId, journeyId: command && command.journeyId,
    });
    const ctx = this._preflight({ ...command, kind: 'complete' }, [
      STATES.IN_PROGRESS,
    ]);
    const prev = ctx.currentState;
    this._engine.complete(command.journeyId);
    return this._result({
      kind: COMMAND_KINDS.COMPLETE, command,
      prevState: prev, nextState: STATES.COMPLETED,
      status: COMMAND_STATUSES.COMMITTED,
    });
  }
}

export { JourneyCommands, COMMAND_KINDS, COMMAND_STATUSES };
